import React, { useState, useEffect, useCallback } from 'react';
import { usePermissions } from '../contexts/PermissionContext';
import { productApi } from '../api';
import PermissionAction from './PermissionAction';
import { useOfflineCollection } from '../hooks/useOfflineCollection';
import { ENTITY_TYPES, SYNC_STATUS } from '../db';
import { dataService } from '../services/dataService';
import { syncService } from '../services/sync/syncService';
import { useOfflineOperations } from '../hooks/useOfflineOperations';
import { showToast, showConfirm } from '../utils/toast';

const ProductManagement = ({ user }) => {
  const { hasPermission } = usePermissions();
  const [products, setProducts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    unit: 'piece',
    discountPercent: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);

  const isNetworkError = (error) => {
    if (!error) return false;
    const message = error.message || error.toString();
    return message.includes('Failed to fetch') || message.includes('NetworkError') || message.includes('request') && message.includes('failed');
  };

  const {
    canPerformOperation: canManageProducts,
    message: productOfflineMessage
  } = useOfflineOperations('products');

  const {
    data: offlineProducts,
    loading: productsLoading,
    refresh: refreshProducts
  } = useOfflineCollection({
    entityType: ENTITY_TYPES.PRODUCT,
    fetcher: () => productApi.list(),
    orgId: user?.orgId
  });

  const removeProductLocally = useCallback((productId) => {
    if (!productId) return;
    setProducts((prev) =>
      prev.filter((product) => {
        if (!product) return false;
        const currentId = product.productId || product.id;
        return currentId !== productId;
      })
    );
  }, []);

  useEffect(() => {
    setProducts(Array.isArray(offlineProducts) ? offlineProducts : []);
  }, [offlineProducts]);

  // Filter products based on search
  useEffect(() => {
    const normalizedSearch = (searchTerm || '').toLowerCase();
    const filtered = products
      .filter((product) => !!product)
      .filter(product => {
        const name = (product.name || '').toLowerCase();
        const description = (product.description || '').toLowerCase();
        return name.includes(normalizedSearch) || description.includes(normalizedSearch);
      });
    setFilteredProducts(filtered);
  }, [products, searchTerm]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.price) {
      showToast({ message: 'Please fill in product name and price', variant: 'warning' });
      return;
    }

    if (!canManageProducts) {
      showToast({
        message: productOfflineMessage || 'Offline product management is not available for your plan.',
        variant: 'warning',
      });
      return;
    }

    try {
    const payload = {
      name: formData.name.trim(),
      description: formData.description || '',
      price: Number(formData.price),
      category: formData.category || '',
      unit: formData.unit || 'piece',
    };
    
    // Only include discountPercent if it's provided and not empty
    if (formData.discountPercent && formData.discountPercent.trim() !== '') {
      payload.discountPercent = Number(formData.discountPercent);
    } else {
      payload.discountPercent = 0;
    }

    if (editingProduct) {
      if (editingProduct?.syncStatus && editingProduct.syncStatus !== SYNC_STATUS.SYNCED) {
        await dataService.upsertLocal(ENTITY_TYPES.PRODUCT, { ...editingProduct, ...payload }, { syncStatus: editingProduct.syncStatus });
        showToast({ message: 'Product updated offline. It will sync when online.', variant: 'success' });
      } else if (!navigator.onLine) {
        await syncService.queueUpdate(ENTITY_TYPES.PRODUCT, editingProduct.productId, payload);
        showToast({ message: 'Product update saved offline. It will sync when online.', variant: 'success' });
      } else {
        try {
          const updated = await productApi.update(editingProduct.productId, payload);
          if (updated) {
            await dataService.upsertFromServer(ENTITY_TYPES.PRODUCT, [updated], { orgId: user?.orgId });
          }
          showToast({ message: 'Product updated successfully', variant: 'success' });
        } catch (error) {
          if (isNetworkError(error)) {
            await syncService.queueUpdate(ENTITY_TYPES.PRODUCT, editingProduct.productId, payload);
            showToast({ message: 'Product update saved offline. It will sync when online.', variant: 'success' });
          } else {
            throw error;
          }
        }
      }
    } else {
      if (!navigator.onLine) {
        await syncService.queueCreate(ENTITY_TYPES.PRODUCT, payload, { orgId: user?.orgId });
        showToast({ message: 'Product saved offline. It will sync when online.', variant: 'success' });
      } else {
        try {
          const created = await productApi.create(payload);
          if (created) {
            await dataService.upsertFromServer(ENTITY_TYPES.PRODUCT, [created], { orgId: user?.orgId });
          }
          showToast({ message: 'Product added successfully', variant: 'success' });
        } catch (error) {
          if (isNetworkError(error)) {
            await syncService.queueCreate(ENTITY_TYPES.PRODUCT, payload, { orgId: user?.orgId });
            showToast({ message: 'Product saved offline. It will sync when online.', variant: 'success' });
          } else {
            throw error;
          }
        }
      }
    }

    setShowModal(false);
    setEditingProduct(null);
    setFormData({ name: '', description: '', price: '', category: '', unit: 'piece', discountPercent: '' });
    await refreshProducts({ force: true });
    } catch (error) {
      console.error('Failed to save product:', error);
      showToast({ message: 'Failed to save product', variant: 'error' });
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description || '',
      price: product.price.toString(),
      category: product.category || '',
      unit: product.unit || 'piece',
      discountPercent: product.discountPercent ? product.discountPercent.toString() : ''
    });
    setShowModal(true);
  };

  const handleDelete = async (productId) => {
    const confirmed = await showConfirm({
      title: 'Delete product?',
      message: 'Are you sure you want to delete this product?',
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      variant: 'warning',
      destructive: true,
    });
    if (!confirmed) {
      return;
    }

    if (!canManageProducts) {
      showToast({
        message: productOfflineMessage || 'Offline product management is not available for your plan.',
        variant: 'warning',
      });
      return;
    }

    try {
      if (!navigator.onLine) {
        await syncService.queueDelete(ENTITY_TYPES.PRODUCT, productId);
        await dataService.removeEntity(ENTITY_TYPES.PRODUCT, productId);
        showToast({ message: 'Product deletion queued. It will sync when online.', variant: 'success' });
        removeProductLocally(productId);
      } else {
        try {
          await productApi.delete(productId);
          showToast({ message: 'Product deleted successfully', variant: 'success' });
          await dataService.removeEntity(ENTITY_TYPES.PRODUCT, productId);
          removeProductLocally(productId);
          await refreshProducts({ force: true });
        } catch (error) {
          if (isNetworkError(error)) {
            await syncService.queueDelete(ENTITY_TYPES.PRODUCT, productId);
            await dataService.removeEntity(ENTITY_TYPES.PRODUCT, productId);
            showToast({ message: 'Product deletion queued. It will sync when online.', variant: 'success' });
            removeProductLocally(productId);
          } else {
            throw error;
          }
        }
      }
    } catch (error) {
      console.error('Failed to delete product:', error);
      showToast({ message: 'Failed to delete product', variant: 'error' });
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingProduct(null);
    setFormData({ name: '', description: '', price: '', category: '', unit: 'piece', discountPercent: '' });
  };

  const loading = productsLoading;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Products & Services</h1>
          <p className="text-gray-600">Manage your products and services for fast billing</p>
        </div>
        
        <PermissionAction actionKey="products:create">
          <button
            onClick={() => setShowModal(true)}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Product
          </button>
        </PermissionAction>
      </div>

    {!canManageProducts && (
      <div className="mb-4 bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-lg px-3 py-2 text-sm">
        {productOfflineMessage || 'Offline product management is not available for your plan.'}
      </div>
    )}

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
          <svg className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
      </div>

      {/* Products Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredProducts.map((product) => (
          <div
            key={product.productId || product.id}
            className={`bg-white border rounded-lg p-4 transition-shadow ${product.syncStatus && product.syncStatus !== SYNC_STATUS.SYNCED ? 'border-yellow-300 bg-yellow-50/70' : 'border-gray-200 hover:shadow-md'}`}
          >
            <div className="flex justify-between items-start mb-3">
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 text-lg flex items-center gap-2">
                  <span>{product.name}</span>
                  {product.syncStatus && product.syncStatus !== SYNC_STATUS.SYNCED && (
                    <span className="inline-flex items-center gap-1 text-[10px] text-yellow-800 bg-yellow-200/90 px-2 py-0.5 rounded-full">
                      ⏳ Pending sync
                    </span>
                  )}
                </h3>
                {product.description && (
                  <p className="text-gray-600 text-sm mt-1">{product.description}</p>
                )}
              </div>
              <div className="flex gap-2 ml-4">
                <PermissionAction actionKey="products:edit">
                  <button
                    onClick={() => handleEdit(product)}
                    disabled={product.syncStatus && product.syncStatus !== SYNC_STATUS.SYNCED}
                    className={`p-1 ${product.syncStatus && product.syncStatus !== SYNC_STATUS.SYNCED ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:text-blue-800'}`}
                    title="Edit"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                </PermissionAction>
                <PermissionAction actionKey="products:delete">
                  <button
                    onClick={() => handleDelete(product.productId || product.id)}
                    disabled={product.syncStatus && product.syncStatus !== SYNC_STATUS.SYNCED}
                    className={`p-1 ${product.syncStatus && product.syncStatus !== SYNC_STATUS.SYNCED ? 'text-gray-400 cursor-not-allowed' : 'text-red-600 hover:text-red-800'}`}
                    title="Delete"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </PermissionAction>
              </div>
            </div>
            
            <div className="flex justify-between items-center">
              <div>
                <span className="text-2xl font-bold text-green-600">₹{product.price}</span>
                <span className="text-gray-500 text-sm ml-2">per {product.unit}</span>
                {product.discountPercent > 0 && (
                  <div className="text-xs text-green-600 mt-1">
                    {product.discountPercent}% discount
                  </div>
                )}
              </div>
              {product.category && (
                <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs">
                  {product.category}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {filteredProducts.length === 0 && (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No products found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm ? 'Try adjusting your search terms.' : 'Get started by adding your first product.'}
          </p>
          {!searchTerm && (
            <div className="mt-6">
              <PermissionAction actionKey="products:create">
                <button
                  onClick={() => setShowModal(true)}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                >
                  Add Product
                </button>
              </PermissionAction>
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">
                  {editingProduct ? 'Edit Product' : 'Add Product'}
                </h2>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Product/Service Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="e.g., Web Development, Consulting"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    rows={3}
                    placeholder="Brief description of the product/service"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Price *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="0.00"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Unit
                    </label>
                    <select
                      value={formData.unit}
                      onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      <option value="piece">Piece</option>
                      <option value="hour">Hour</option>
                      <option value="day">Day</option>
                      <option value="month">Month</option>
                      <option value="kg">Kilogram</option>
                      <option value="liter">Liter</option>
                      <option value="meter">Meter</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Default Discount (%)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={formData.discountPercent}
                    onChange={(e) => setFormData({ ...formData, discountPercent: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                  <p className="text-xs text-gray-500 mt-1">Optional: Default discount percentage for this product</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="">Select Category</option>
                    <option value="Product">Product</option>
                    <option value="Service">Service</option>
                  </select>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    {editingProduct ? 'Update' : 'Add'} Product
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductManagement;
